import { useWindowVirtualizer, VirtualItem } from '@tanstack/react-virtual';
import classNames from 'classnames';
import { ReactNode, useCallback, useEffect, useRef } from 'react';
import { TagCombobox, TagEntityType } from '~/components/TagCombobox';
import { Result } from '~/components/ui/Result';
import { Tag } from '~/gql/graphql';
import { FloatingRefreshButton } from './FloatingRefreshButton';
import { MarkAllAsReadButton } from './MarkAllAsReadButton';

export type VirtualListProps = {
  /** The list of items to render (activity edges or date separator strings) */
  listToRender: Array<unknown>;
  /** Whether there are more pages to load */
  hasNextPage: boolean;
  /** Total count for display */
  displayCount: number;
  /** Unread count for display */
  unreadDisplayCount: number;
  /** Total unread count (for MarkAllAsRead button visibility) */
  totalsUnread: number;
  /** Latest activity ID for mark all as read */
  latest?: string;
  /** Count of new activities since last refresh */
  newActivitiesCount: number;
  /** Current search query (for UI feedback) */
  debouncedQuery?: string;
  /** Whether background loading is active (loading more pages while searching) */
  isBackgroundLoading?: boolean;
  /** Number of activities searched so far (for progress) */
  searchedCount?: number;
  /** Total count of activities to search */
  totalToSearch?: number;
  /** Tags for the TagCombobox */
  activityTags: Tag[];
  /** Currently selected tag IDs */
  selectedTagsIds: string[];
  /** Whether the tag box is open */
  isTagBoxOpen: boolean;
  /** Callback when tag box closes */
  onTagBoxClose: () => void;
  /** Callback when tags change */
  onTagsChange: (tags: Tag[]) => void;
  /** Callback to submit tag changes */
  onTagSubmit: () => void;
  /** Callback when "new updates" button is clicked */
  onScrollToTop: () => void;
  /** Callback when mark all as read succeeds */
  onMarkAllAsReadOptimistic: () => void;
  /** Callback for infinite scroll - called when approaching end of list */
  onLoadMore: () => void;
  /** Render function for each row */
  renderRow: (params: {
    virtualRow: VirtualItem;
    measureElement: (el: HTMLElement | null) => void;
  }) => ReactNode;
  /** Whether the refresh button is loading new data */
  isRefreshing?: boolean;
};

/**
 * MobileActivityList - Virtualized list for mobile using window scroll.
 * Uses useWindowVirtualizer to scroll the entire window rather than a container.
 */
export const MobileActivityList = ({
  listToRender,
  hasNextPage,
  displayCount,
  unreadDisplayCount,
  totalsUnread,
  latest,
  newActivitiesCount,
  debouncedQuery,
  // isBackgroundLoading - not used, we rely on hasNextPage for search indicator
  searchedCount,
  totalToSearch,
  activityTags,
  selectedTagsIds,
  isTagBoxOpen,
  onTagBoxClose,
  onTagsChange,
  onTagSubmit,
  onScrollToTop,
  onMarkAllAsReadOptimistic,
  onLoadMore,
  renderRow,
  isRefreshing = false,
}: VirtualListProps) => {
  // Server-side search supports pagination, so always include loader row when more pages exist
  const virtualCount = hasNextPage
    ? listToRender.length + 1
    : listToRender.length;

  const rowVirtualizer = useWindowVirtualizer({
    count: virtualCount,
    estimateSize: () => 75,
    overscan: 5,
    paddingStart: 56, // 3.5rem
  });

  // Use virtualizer's built-in range to trigger infinite scroll
  // This is more reliable than tracking index in render callbacks via setTimeout
  // Works with server-side search since pagination is supported
  const loadMoreCalledRef = useRef(false);
  const range = rowVirtualizer.range;

  useEffect(() => {
    // Reset the called flag when list length changes (new data loaded)
    loadMoreCalledRef.current = false;
  }, [listToRender.length]);

  useEffect(() => {
    // Skip if no range, already called, or no more pages
    if (!range || loadMoreCalledRef.current || !hasNextPage) {
      return;
    }
    // Trigger load when within 5 items of the end
    if (range.endIndex >= listToRender.length - 5) {
      loadMoreCalledRef.current = true;
      onLoadMore();
    }
  }, [range, hasNextPage, listToRender.length, onLoadMore]);

  const handleScrollToTop = useCallback(() => {
    // Use behavior: 'auto' for instant position calculation, avoiding measurement drift
    // during smooth scroll. For window scroll, CSS scroll-behavior on html/body handles smoothness.
    rowVirtualizer.scrollToIndex(0, {
      align: 'start',
      behavior: 'auto',
    });
    onScrollToTop();
  }, [rowVirtualizer, onScrollToTop]);

  return (
    <div
      style={{
        height: rowVirtualizer.getTotalSize(),
        minHeight: '100vh',
        width: '100%',
        position: 'relative',
      }}
    >
      <div className='flex h-14 items-center overflow-hidden px-4 py-2 lg:pl-12'>
        <Result
          count={displayCount}
          className='!pb-0 text-xs leading-7 text-secondary lg:text-base'
        />{' '}
        <div className='flex-1 whitespace-nowrap pl-1 text-xs leading-7 text-secondary lg:text-base'>
          ({unreadDisplayCount} unread)
          {/* Search progress indicator - shows while searching and after all pages loaded */}
          {debouncedQuery &&
            searchedCount !== undefined &&
            totalToSearch !== undefined && (
              <span
                className={classNames(
                  'ml-2 text-brand transition-opacity duration-300',
                  hasNextPage && 'animate-pulse-opacity'
                )}
              >
                {hasNextPage
                  ? `Searching ${searchedCount.toLocaleString()} of ${totalToSearch.toLocaleString()}...`
                  : `Searched ${searchedCount.toLocaleString()} of ${totalToSearch.toLocaleString()}`}
              </span>
            )}
        </div>
        <div className='flex-1 text-right'>
          {totalsUnread > 0 && (
            <MarkAllAsReadButton
              latest={latest}
              unread={totalsUnread}
              onSuccess={onMarkAllAsReadOptimistic}
            />
          )}
        </div>
      </div>

      <FloatingRefreshButton
        count={newActivitiesCount}
        onClick={handleScrollToTop}
        topOffset='2rem'
        isLoading={isRefreshing}
      />

      {/* TagCombobox overlay */}
      <div
        className={classNames(
          'sticky top-0 z-50 w-full',
          isTagBoxOpen && 'h-screen px-10 pt-5'
        )}
        onClick={onTagBoxClose}
      >
        <div className='ml-auto w-fit' onClick={(e) => e.stopPropagation()}>
          <TagCombobox
            entityType={TagEntityType.Activity}
            options={activityTags}
            value={activityTags.filter((t) => selectedTagsIds.includes(t.id))}
            onChange={onTagsChange}
            dialogMode={true}
            isOpen={isTagBoxOpen}
            onClose={() => {
              onTagBoxClose();
            }}
            onSubmit={onTagSubmit}
            containerStyles='w-80'
          />
        </div>
      </div>
      {/* End of TagCombobox overlay */}

      {rowVirtualizer.getVirtualItems().map((virtualRow) =>
        renderRow({
          virtualRow,
          measureElement: rowVirtualizer.measureElement,
        })
      )}
    </div>
  );
};

export default MobileActivityList;
